// apiService.js
import axios from "axios";
import CryptoJS from "crypto-js";
import { EMPTY, from, of } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { SmartLoader } from "soft_digi";
import config from "../config/config";
import { showNotification } from "./notifyService";
import { getCsrf, getToken, getUserSession, updateToken, setUserSession } from "./sessionService";

const _KEY = config.ENCRYPTION_KEY || "";
const ENCRYPTION = config.ENCRYPTION ?? false;

interface CipherParams {
  ciphertext: any;
  iv?: any;
  salt?: any;
}

const CryptoJSAesJson = {
  stringify: function (cipherParams: CipherParams): string {
    const json: { ct: string; iv?: string; s?: string } = {
      ct: cipherParams.ciphertext.toString(CryptoJS.enc.Base64),
    };

    if (cipherParams.iv) json.iv = cipherParams.iv.toString();
    if (cipherParams.salt) json.s = cipherParams.salt.toString();

    return JSON.stringify(json);
  },
  parse: function (jsonStr: string): CryptoJS.lib.CipherParams {
    const json = JSON.parse(jsonStr);
    const cipherParams = CryptoJS.lib.CipherParams.create({
      ciphertext: CryptoJS.enc.Base64.parse(json.ct),
    });

    if (json.iv) cipherParams.iv = CryptoJS.enc.Hex.parse(json.iv);
    if (json.s) cipherParams.salt = CryptoJS.enc.Hex.parse(json.s);

    return cipherParams;
  },
};

const encryptPayload = (data: any) => {
  let encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), _KEY, {
    format: CryptoJSAesJson,
  }).toString();
  // console.log(encrypted);
  return btoa(encrypted);
};

const decrypt_data = (encrypted: string) => {
  var decrypted = JSON.parse(
    CryptoJS.AES.decrypt(encrypted, _KEY, {
      format: CryptoJSAesJson,
    }).toString(CryptoJS.enc.Utf8)
  );
  return decrypted;
};

const decrypted_data_new = (encr: string) => {
  const encryptedJson = atob(encr);
  const jsonData = JSON.parse(encryptedJson);
  //console.log("encr string ", encr);
  //console.log(" bytes ", encryptedJson);
  //console.log("enc key " ,  _KEY);
  try {
    const decrypted = CryptoJS.AES.decrypt(JSON.stringify(jsonData), _KEY, {
      format: CryptoJSAesJson,
    });
   // console.log("decrypted ", decrypted);
    const plaintext = decrypted.toString(CryptoJS.enc.Utf8);
  //  console.log("plan text ", plaintext);
    return JSON.parse(plaintext);
  } catch (err) {
    console.log("Standard decryption failed, trying alternative method:", err);
  }
};

const getPayLoad = (data_in: any) => {
  //console.log("data in ", data_in);
  data_in["timestamp"] = Date.now();
  data_in["timeszone"] = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (ENCRYPTION) {
    let encr_data = encryptPayload(data_in);
    return { encryptedData: encr_data };
  } else {
    return data_in;
  }
};

const API = axios.create({
  baseURL: config.API_URL,
  withCredentials: true,
});

// Track if a refresh is already in progress to avoid multiple simultaneous refreshes
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
};

// 401 Interceptor - auto-refresh token on expiry
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only handle 401 errors, skip if already retried or if it's the refresh endpoint itself
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("auth/refresh") &&
      !originalRequest.url?.includes("auth/login")
    ) {
      if (isRefreshing) {
        // Queue this request until refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers["Authorization"] = "Bearer " + token;
              resolve(API(originalRequest));
            },
            reject: (err: any) => {
              reject(err);
            },
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshTokenStr = getUserSession("refresh_token");
        if (!refreshTokenStr) {
          throw new Error("No refresh token");
        }

        const response = await API.post("auth/refresh", {
          refresh_token: refreshTokenStr,
        });

        const newAccessToken = response.data?.access_token || response.data?.data?.access_token;
        if (newAccessToken) {
          updateToken(newAccessToken);
          processQueue(null, newAccessToken);
          originalRequest.headers["Authorization"] = "Bearer " + newAccessToken;
          return API(originalRequest);
        } else {
          throw new Error("No access token in refresh response");
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Clear session and redirect to login
        setUserSession(null);
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

const getDecryptedResponse = (response: any) => {
  if (ENCRYPTION && response.data) {
    let decrypted = decrypted_data_new(response.data.data);
   // console.log("decrypted ", decrypted);
    // let decoded = CryptoJS.enc.Utf8.stringify(
    //   CryptoJS.enc.Base64.parse(response.data.data)
    // );
    // console.log("decoded" , decoded);
    // let decrypted = decrypt_data(JSON.parse(decoded));
     response.data.data = decrypted;
  }
  //console.log(response);
  return response;
};

const processError = (error: any) => {
  let msg = "Some Thing Went Wrong";
  // console.log("code " , error);
  if (error.code === "ERR_BAD_REQUEST") {
    let out = getDecryptedResponse(error?.response);
    msg = out.data && out.data.message ? out.data.message : "Invalid Request";
    return msg;
  } else if (error.code === "ERR_BAD_RESPONSE") {
    let out = getDecryptedResponse(error?.response);
    msg = out.data && out.data.message ? out.data.message : "Invalid Request";
    if (out.data && out.data.msg) {
      msg = out.data.msg;
    }
    return msg;
  }
  //console.log("message " , msg);
  return msg;
};

// Default error handler
const defaultErrorHandler = (errorMessage: string, error: any) => {
  // SmartLoader.hideLoader();
  showNotification("error", errorMessage);
  //console.error('API Request Error:', error);
  // alert(errorMessage);
  return EMPTY;
};

interface ApiOptions {
  handleError?: (msg: string, obj: any) => void;
  requiresAuthorization?: boolean | undefined;
  customHeaders?: any;
  showLoading?: boolean;
  loadingMsg?: string;
}

const get = (url: string, options?: ApiOptions) => {
  const {
    requiresAuthorization = true,
    customHeaders = {},
    showLoading = true,
    loadingMsg = "Fetching... Please Wait",
    handleError,
  } = options || {};
  // Check if authorization is Required and if a token is available
  if (requiresAuthorization) {
    customHeaders["Authorization"] = "Bearer " + getToken();
  }
  if (showLoading) {
    SmartLoader.showLoader(loadingMsg);
  }
  return from(API.get(url, { headers: customHeaders })).pipe(
    catchError((error) => {
      if (showLoading) {
        SmartLoader.hideLoader();
      }
      let errorMessage = processError(error);
      const errorHandler = handleError ? handleError : defaultErrorHandler;
      errorHandler(errorMessage, error);
      return EMPTY;
    }),
    switchMap((response) => {
      response = getDecryptedResponse(response);
      if (showLoading) {
        SmartLoader.hideLoader();
      }
      return of(response); // For example, just returning the response as is
    })
  );
};

const post = (url: string, data: any, options?: ApiOptions) => {
  const {
    requiresAuthorization = true,
    customHeaders = {},
    showLoading = true,
    loadingMsg = "Fetching... Please Wait",
    handleError,
  } = options || {};
  // Check if authorization is Required and if a token is available
  if (requiresAuthorization) {
    // console.log("eneterd insude " , getToken())
    customHeaders["Authorization"] = "Bearer " + getToken();
  }
  customHeaders["X-CSRF-Token"] = getCsrf();
  if (showLoading) {
    SmartLoader.showLoader(loadingMsg);
  }
  // console.log("headers " , customHeaders);
  return from(API.post(url, getPayLoad(data), { headers: customHeaders })).pipe(
    catchError((error) => {
      //if (showLoading) {
      SmartLoader.hideLoader();
      // }
      let errorMessage = processError(error);
      const errorHandler = handleError ? handleError : defaultErrorHandler;
      errorHandler(errorMessage, error);
      return EMPTY;
    }),
    switchMap((response) => {
      if (showLoading) {
        SmartLoader.hideLoader();
      }
      response = getDecryptedResponse(response);
      // check for msg
      if (response && response.data && response.data.msg) {
        showNotification("success", response.data.msg);
      }
      return of(response); // For example, just returning the response as is
    })
  );
};

const put = (url: string, data: any, options?: ApiOptions) => {
  const {
    requiresAuthorization = true,
    customHeaders = {},
    showLoading = false,
    loadingMsg = "Saving...",
    handleError,
  } = options || {};
  if (requiresAuthorization) {
    customHeaders["Authorization"] = "Bearer " + getToken();
  }
  customHeaders["X-CSRF-Token"] = getCsrf();
  if (showLoading) SmartLoader.showLoader(loadingMsg);
  return from(API.put(url, getPayLoad(data), { headers: customHeaders })).pipe(
    catchError((error) => {
      SmartLoader.hideLoader();
      const errorHandler = handleError ? handleError : defaultErrorHandler;
      errorHandler(processError(error), error);
      return EMPTY;
    }),
    switchMap((response) => {
      if (showLoading) SmartLoader.hideLoader();
      response = getDecryptedResponse(response);
      if (response && response.data && response.data.msg) {
        showNotification("success", response.data.msg);
      }
      return of(response);
    })
  );
};

export { get, post, put };

